import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { ReportStatus } from '@signalvote/shared';
import { useFinanceReports } from '@/hooks/queries/useFinanceReports';
import { useFinanceHealth } from '@/hooks/queries/useFinanceDeadlines';
import { cn } from '@/lib/utils';
import { useCampaignStore } from '@/stores';
import { hasReopenControl } from '../reports/reportUtils';
import { formatDate } from '../utils';
import { useHasQueuedObligation } from './useHasQueuedObligation';

/**
 * Shown on the Close hub when NO reporting period is currently open — right
 * after filing early, or in the gap between periods. The old surface here was a
 * single cold gray sentence ("No reporting period is currently open."), which
 * read as if the treasurer's data had vanished. This replaces it with a calm,
 * plainly-worded reassurance: you're caught up, nothing is due, and everything
 * you recorded is still on screen (the ledger stays mounted below this card).
 *
 * Copy is deliberately warm and jargon-free for non-technical / elderly
 * treasurers — no "period", "deadline", or error codes.
 *
 * Two actions:
 *   - "View filed reports" → the reports list.
 *   - "Reopen last period" → the most-recent filed report's detail. Only rendered
 *     when that page actually shows a reopen control the user may use.
 *
 * The caught-up CLAIM is withheld while a filed report needs an amendment. That card sits
 * directly above this one on the close hub, so asserting "you're all caught up" one row
 * under a card naming a report that is waiting to be re-filed would be exactly the false
 * reassurance the amendment card exists to end. The data reassurance ("everything you
 * recorded is still here") is the honest part and always stays.
 */
export function CloseCaughtUpEmptyState() {
  const navigate = useNavigate();
  // The most-recent FILED report, so we can name it, show its filed date, and
  // point Reopen at it. The list endpoint sorts by recency, so items[0] is the
  // latest — pageSize:1 keeps it a single row.
  const { data: filedReports } = useFinanceReports({
    status: ReportStatus.Filed,
    pageSize: 1,
  });
  const lastFiled = filedReports?.items?.[0] ?? null;
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const reopenTarget = canManage && lastFiled && hasReopenControl(lastFiled) ? lastFiled : null;
  // Same payload the amendment card above reads, so the two can never disagree.
  // Unanswered health leaves the amendment line out; the masthead says health failed.
  const health = useFinanceHealth();
  const amendmentOutstanding = health.status === 'ready' && health.data.reportsNeedingAmendment.length > 0;
  // A due 48-hour notice, rapid or daily report sits in the action queue above this card.
  const obligationQueued = useHasQueuedObligation();

  return (
    <div
      data-slot="close-caught-up"
      className={cn(
        'mx-auto flex w-full max-w-md flex-col items-center gap-4 rounded-2xl',
        'border border-slate-200 bg-white p-8 text-center text-card-foreground shadow-sm',
      )}
    >
      <h2 className="text-xl font-semibold text-foreground">
        {amendmentOutstanding
          ? 'No new report is due'
          : obligationQueued
            ? 'No regular report is due'
            : "You're all caught up"}
      </h2>

      {obligationQueued && !amendmentOutstanding ? (
        <p data-testid="close-caught-up-body" className="text-base leading-relaxed text-muted-foreground">
          There&apos;s no open period, but a filing shown above is still due. Everything
          you&apos;ve recorded is still here, shown below.
        </p>
      ) : amendmentOutstanding ? (
        <p data-testid="close-caught-up-body" className="text-base leading-relaxed text-muted-foreground">
          There&apos;s no open period, but a report you already filed still needs an
          amendment, shown above. Everything you&apos;ve recorded is still here, shown below.
        </p>
      ) : lastFiled ? (
        <p data-testid="close-caught-up-body" className="text-base leading-relaxed text-muted-foreground">
          Nothing is due right now. Your last report
          {lastFiled.name ? (
            <>
              {' — '}
              <span className="font-medium text-foreground">{lastFiled.name}</span>
            </>
          ) : null}
          {lastFiled.filedDate ? ` — was filed on ${formatDate(lastFiled.filedDate)}.` : '.'}{' '}
          Everything you've recorded is still here, shown below.
        </p>
      ) : (
        <p data-testid="close-caught-up-body" className="text-base leading-relaxed text-muted-foreground">
          Nothing is due right now. When a reporting period opens it'll appear
          here — and everything you record is always saved, shown below.
        </p>
      )}

      <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
        <Button asChild variant="outline">
          <Link to="/admin/finance/reports">View filed reports</Link>
        </Button>
        {reopenTarget && (
          <Button
            type="button"
            onClick={() =>
              void navigate({
                to: '/admin/finance/reports/$reportId',
                params: { reportId: reopenTarget.id },
              })
            }
          >
            Reopen last period
          </Button>
        )}
      </div>
    </div>
  );
}
